import { Post, UserStreak, StateData, UTMResponse, DailyProgressResponse } from '@/types/streaks';

const BASE_URL = 'https://backend.testeswaffle.org/metrics/streaks';

export const getHeaders = (): Record<string, string> => {
    try {
        const userJson = localStorage.getItem('user');
        if (!userJson) {
            return {
                'Content-Type': 'application/json'
            };
        }

        const user = JSON.parse(userJson);
        return {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
        };
    } catch {
        return {
            'Content-Type': 'application/json'
        };
    }
};

export const fetchPosts = async (): Promise<Post[]> => {
    try {
        const response = await fetch(`${BASE_URL}/posts`, { headers: getHeaders() });
        if (!response.ok) return [];
        const data = await response.json();
        return data.data || [];
    } catch {
        return [];
    }
};

export const fetchTopStreaks = async (): Promise<{ users: UserStreak[], totalUsersWithStreaks: number }> => {
    try {
        const response = await fetch(`${BASE_URL}/top`, { headers: getHeaders() });
        if (!response.ok) return { users: [], totalUsersWithStreaks: 0 };
        const data = await response.json();
        return data.data || { users: [], totalUsersWithStreaks: 0 };
    } catch {
        return { users: [], totalUsersWithStreaks: 0 };
    }
};

export const fetchStateData = async (): Promise<StateData[]> => {
    try {
        const response = await fetch(`${BASE_URL}/by-state/readers`, { headers: getHeaders() });
        if (!response.ok) return [];
        const data = await response.json();
        return data.data?.results || [];
    } catch {
        return [];
    }
};

export const fetchUTMsData = async (): Promise<UTMResponse['data']> => {
    const emptyData = {
        overview: {
            totalUsers: 0,
            totalStates: 0,
            totalSharingUsers: 0,
            totalPaidUsers: 0,
            averageShares: 0
        },
        sourceAnalytics: [] as Array<{
            source: string;
            metrics: {
                users: number;
                states: number;
                sharingUsers: number;
                paidUsers: number;
                avgSharesPerUser: number;
            };
            dates: {
                firstUser: string;
                lastUser: string;
            };
        }>,
        timeSeriesData: {},
        geographicData: {} as Record<string, Record<string, { users: number; sharingUsers: number; }>>
    };

    try {
        const response = await fetch(`${BASE_URL}/utms`, { headers: getHeaders() });
        if (!response.ok) return emptyData;
        const data = await response.json();
        return data.data || emptyData;
    } catch {
        return emptyData;
    }
};

export const registerHistoricalRead = async (email: string, postId: string): Promise<any> => {
    try {
        const response = await fetch(`${BASE_URL}/historical-read`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ email, postId })
        });
        if (!response.ok) return null;
        const data = await response.json();
        return data.data;
    } catch {
        return null;
    }
};

export const fetchLastPosts = async (): Promise<Array<{
    postId: string;
    publishDate: string;
    title: string;
    url: string;
}>> => {
    try {
        const response = await fetch(`${BASE_URL}/last-posts`, { headers: getHeaders() });
        if (!response.ok) return [];
        const data = await response.json();
        return data.data || [];
    } catch {
        return [];
    }
};

export const fetchDailyProgress = async (startDate: string, endDate: string): Promise<DailyProgressResponse['data']> => {
    try {
        const response = await fetch(`${BASE_URL}/daily-progress?startDate=${startDate}&endDate=${endDate}`, { 
            headers: getHeaders() 
        });
        if (!response.ok) return {
            startDate,
            endDate,
            totalReads: 0,
            activeReaders: 0,
            averageStreak: 0,
            topStreak: 0,
            dailyProgress: []
        };
        const data = await response.json();
        return data.data;
    } catch {
        return {
            startDate,
            endDate,
            totalReads: 0,
            activeReaders: 0,
            averageStreak: 0,
            topStreak: 0,
            dailyProgress: []
        };
    }
};

export const fetchBins = async (): Promise<Array<{ range: string; num_users: number }>> => {
    try {
        const response = await fetch(`${BASE_URL}/bins`, { headers: getHeaders() });
        if (!response.ok) return [];
        const data = await response.json();
        return data.data?.bins || [];
    } catch {
        return [];
    }
}; 